import { Button, Dialog, DialogContent, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useExpressFleetMallsConfig } from "@saleor/autobooking/queries";
import { useModalStyles } from "@saleor/autobooking/styles/modalStyles";
import { maybe } from "@saleor/misc";
import { DialogProps } from "@saleor/types";
import {
  HasAutobookingConfigInput,
  MallSortField,
  OrderDirection
} from "@saleor/types/globalTypes";
import React from "react";

import MallsListForm from "./components/MallListForm/MallsListForm";

export interface MallsDialogProps extends DialogProps {
  modalText: string;
  modalTitle: string;
  confirmLabel: string;
  confirmLabelBgColor?: string;
  cancelLabel: string;
  filterAutoBooking: HasAutobookingConfigInput;
  handleConfirm: (mallIds: string[]) => void;
  handleCancel: () => void;
}

const MallsDialog: React.FC<MallsDialogProps> = props => {
  const {
    onClose,
    open,
    modalText,
    modalTitle,
    confirmLabel,
    confirmLabelBgColor,
    cancelLabel,
    filterAutoBooking,
    handleConfirm,
    handleCancel
  } = props;

  const classes = useModalStyles(props);

  const [searchText, setSearchText] = React.useState("");
  const [selectedMalls, setSelectedMalls] = React.useState([]);
  const [selectAll, setSelectAll] = React.useState(false);

  const sortBy = {
    sortBy: {
      direction: OrderDirection.ASC,
      field: MallSortField.NAME
    }
  };

  const { data, refetch } = useExpressFleetMallsConfig({
    displayLoader: true,
    skip: !open,
    variables: {
      ...sortBy,
      first: 100,
      hasAutobookingActive: filterAutoBooking,
      hasAutobookingConfig: true,
      id: [],
      isServiceable: true,
      search: searchText,
      status: "ACT"
    }
  });
  const malls = maybe(() => data.malls.edges.map(edge => edge.node)) || [];

  React.useEffect(() => {
    setSearchText("");
    setSelectedMalls([]);
    setSelectAll(false);
    if (open) {
      refetch();
    }
  }, [open]);

  return (
    <Dialog
      onClose={onClose}
      open={open}
      maxWidth={false}
      fullWidth={false}
      PaperProps={{
        style: {
          borderRadius: "15px",
          width: "800px"
        }
      }}
    >
      <DialogContent className={classes.modal}>
        <div className={classes.modalHeaderFlex} style={{ height: 60 }}>
          <div>
            <p
              className={`${classes.modalTitleText} ${classes.modalTitleTextMedium}`}
              style={{ textAlign: "left" }}
            >
              {modalTitle}
            </p>
            <p className={`${classes.modalTitleTextSmall}`}>{modalText}</p>
          </div>
          <IconButton
            aria-label="close"
            onClick={() => onClose()}
            className={`${classes.modalCloseBtn} ${classes.paddingTop0}`}
          >
            <CloseIcon />
          </IconButton>
        </div>

        <div className={classes.modalCenter}>
          <MallsListForm
            malls={malls}
            selectedMalls={selectedMalls}
            setSelectedMalls={setSelectedMalls}
            selectAll={selectAll}
            setSelectAll={setSelectAll}
            searchText={searchText}
            setSearchText={setSearchText}
            refetch={refetch}
          />
        </div>

        <div className={`${classes.modalBottom} ${classes.padding0}`}>
          <Button
            id="cancelDialogBtn"
            autoFocus
            onClick={handleCancel}
            color="primary"
            style={{
              backgroundColor: "#F1F2F3",
              marginRight: 10
            }}
            className={classes.btnCancel}
          >
            <span style={{ color: "#747C8C" }}>{cancelLabel}</span>
          </Button>
          <Button
            id="confirmDialogBtn"
            autoFocus
            onClick={() => handleConfirm(selectedMalls)}
            color="primary"
            variant="contained"
            style={{ backgroundColor: confirmLabelBgColor }}
            className={classes.btnYes}
            disabled={selectedMalls.length === 0}
          >
            <span className={classes.txtWhite}>{confirmLabel}</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

MallsDialog.displayName = "MallsDialog";
export default MallsDialog;
